import "./MigrationProgressPage.css";
import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { AppDispatch } from "../../redux/store";
import RootState from "../../redux/states/root.state";
import Header from "../../components/common/Header/Header";
import { fetchSpaUpdateProgress } from "../../redux/actions/spaUpdateProgress.actions";
import { toQuery } from "../../components/common/AutoRefresh/refreshParams";
import MigrationProgressContainer from "./components/MigrationProgressContainer";
import { AlertIcon, InfoIcon, RefreshIcon } from "../../components/common/Icons/Icons";

const MigrationProgressPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const searchParams = useSearchParams()[0];
  const { meta, error } = useSelector((state: RootState) => state.spaUpdateProgress);

  // Derived so that toggling the auto-refresh interval (a client-only param) doesn't
  // count as a filter change and fire an extra fetch.
  const query = useMemo(() => toQuery(searchParams), [searchParams]);

  useEffect(() => {
    dispatch(fetchSpaUpdateProgress({ params: query }));
  }, [dispatch, query]);

  // Auto-refresh reuses whatever filters/sort/page are currently in the URL, and runs
  // silently so rows swap in without the table collapsing to a spinner.
  const handleRefresh = useCallback(() => {
    dispatch(fetchSpaUpdateProgress({ params: query, silent: true }));
  }, [dispatch, query]);

  return (
    <div className="page lp-page">
      <Header
        header="Migration Progress"
        headerExtra={meta?.total !== undefined &&
          <ul className="lp-stats" aria-label="Summary">
            <li className="lp-stat"><RefreshIcon/><b>{(meta?.total ?? 0).toLocaleString()}</b> Migrations</li>
          </ul>
        }
      />
      <main className="lp-main">
        <div className="lp-notice" role="note">
          <InfoIcon/>
          <span>
            Progress reporting is only implemented on tubs migrating from firmware <strong>1.0.53 or later</strong>.
            Tubs migrating from earlier versions do not report progress and will not appear here.
            An absent tub is not evidence of a stalled or failed migration.
          </span>
        </div>

        {error && (
          <div className="lp-notice error" role="alert">
            <AlertIcon/>
            <span>{error}</span>
          </div>
        )}

        <MigrationProgressContainer onRefresh={handleRefresh} />
      </main>
    </div>
  );
};

export default MigrationProgressPage;
